import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {TriggerQueryService} from '@services/trigger/triger-action/trigger-query.service';
import {
  TriggerQueryEditComponent
} from '@components/administration/machine/machine-configuration/trigger/trigger-query/modals/trigger-query-edit/trigger-query-edit.component';
import {Trigger} from '@models/adminstration/machine/trigger/trigger';
import {TRIGGER_ACTION_TYPE} from '@constants/administration/machine/trigger-query/trigger-action-type';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {TriggerHelperService} from '@services/trigger/helper/trigger-helper.service';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-admin-trigger-query-action',
    templateUrl: './trigger-query-action.component.html',
    styleUrls: ['./trigger-query-action.component.scss'],
    standalone: false
})
export class TriggerQueryActionComponent implements OnInit {

  @Input()
  machineId: number | undefined = undefined;

  @Input()
  trigger: Trigger = new Trigger();

  @Input()
  userRole: Role = new Role(new User());

  @Output()
  triggerQueryDeleteEvent: EventEmitter<Trigger> = new EventEmitter<Trigger>();

  @Output()
  triggerQueryUpdatedEvent: EventEmitter<Trigger> = new EventEmitter<Trigger>();

  deleteButtonIsEnabled = false;
  private deleteTriggerQueryMessageTitle = '';
  private deleteTriggerQueryMessageBody = '';
  private deleteTriggerQueryMessageOK = '';
  private COMPONENT_NAME: string = 'TriggerQueryActionComponent';
  private ACCESS_TO_THE_TARGET_RESOURCE_HAS_BENN_DENIED: number = 412;

  constructor(
      private translate: TranslateService,
      private confirmationDialogService: ConfirmationDialogService,
      private loadingIndicator: LoadingIndicatorService,
      private notification: NotificationService,
      private triggerActionService: TriggerQueryService,
      private triggerHelperService: TriggerHelperService,
      private modalService: ModalService,
      private notificationService: NotificationService,
  ) {
    this.deleteTriggerQueryMessageTitle = this.translate.instant('Delete trigger action');
    this.deleteTriggerQueryMessageBody = this.translate.instant('Do you really want to permanently delete the trigger action %s?');
    this.deleteTriggerQueryMessageOK = this.translate.instant('Delete');
  }

  get editButtonIsDisabled(): boolean {
    return !this.userRole.isSuperAdminRole && !this.userRole.isTechAdminRole && this.trigger.triggerActionType === TRIGGER_ACTION_TYPE.PUBSUB;
  }

  ngOnInit(): void {
    this.setDeleteButtonIsEnabled();
  }

  onDeleteTriggerQuery(): void {
    console.log(`${this.COMPONENT_NAME}. removing trigger query: ${this.trigger.name.toBold()}`);
    const deleteMessageBody = this.deleteTriggerQueryMessageBody.replace('%s', this.trigger.name.toBold());
    this.confirmationDialogService.handleConfirmationDialog(this.deleteTriggerQueryMessageTitle, deleteMessageBody, '',
        this.deleteTriggerQueryMessageOK).subscribe(
        {
          next: userConfirmed => {
            if (!userConfirmed || this.machineId == undefined)
              return;

            this.loadingIndicator.show(this.translate.instant('Deleting'));
            this.triggerActionService.delete(this.machineId, this.trigger.id).subscribe(
                {
                  next: () => {
                    this.triggerQueryDeleteEvent.emit(this.trigger);
                    this.loadingIndicator.hide();
                    this.notification.success(this.translate.instant('Trigger action deleted'), this.COMPONENT_NAME, false);
                  }, error: (error) => {
                    this.loadingIndicator.hide();
                    if (error.status === this.ACCESS_TO_THE_TARGET_RESOURCE_HAS_BENN_DENIED) {
                      this.notification.error(this.translate.instant('* this element is currently being used and therefore cannot be changed.'), error,
                          this.COMPONENT_NAME, true);
                    } else {
                      this.notification.error(this.translate.instant('Error deleting trigger action'), error, this.COMPONENT_NAME, true);
                    }
                  }
                });

          }, error: () => {
            this.loadingIndicator.hide();
          }
        });
  }

  onEditTriggerQuery(): void {
    const modalRef = this.modalService.open(TriggerQueryEditComponent, {size: 'lg'});
    modalRef.componentInstance.trigger = this.trigger;
    modalRef.componentInstance.userRole = this.userRole;
    modalRef.result.then(({parentId, request}) => {
      this.loadingIndicator.show(this.translate.instant('Loading'));
      this.triggerActionService.updateTrigger(parentId, request).subscribe(
          {
            next: (trigger: Trigger) => {
              this.triggerQueryUpdatedEvent.emit(trigger);
              const message = this.translate.instant('Successfully created/updated trigger action');
              this.notificationService.success(message, this.COMPONENT_NAME, false);
              this.loadingIndicator.hide();
            }, error: error => {
              console.log(error);
              this.loadingIndicator.hide();
              const message = this.translate.instant('Error creating/updating trigger action');
              this.notificationService.error(message, error, this.COMPONENT_NAME, true);
            }
          });
    });
  }

  private setDeleteButtonIsEnabled(): void {
    this.deleteButtonIsEnabled = this.triggerHelperService.setDeleteButtonIsEnabled(this.trigger, this.userRole);
  }
}
